/* ============================================================================
 * custom-back-in-stock.js
 * ----------------------------------------------------------------------------
 * "Notify me when back in stock" — shows on sold-out variants (per selected
 * size), captures the customer's email, and subscribes it to a Klaviyo list
 * via the client-side Subscribe API. The requested product/variant is attached
 * as a profile property so the store owner sees what each person is waiting for.
 *
 * VARIANT REACTIVITY (mirrors blocks/pre-order behaviour):
 *   Atelier re-renders the buy-buttons block server-side on every size change
 *   and fires `variant:update` on the section, carrying the fresh section HTML
 *   in event.detail.data.html. The <back-in-stock> element is ALWAYS in the
 *   DOM (with [hidden] when the current variant is buyable). On each size
 *   change we read the freshly rendered <back-in-stock> from that HTML and
 *   mirror its [hidden] state + variant data. Server-side Liquid stays the
 *   source of truth (it alone sees variant.inventory_quantity/policy).
 *
 * Both Klaviyo values below are PUBLIC keys (Site ID + List ID) — designed to
 * be exposed in client-side code and safe in a public repo.
 *
 * Author: Sothiares dev team
 * Date: 2026-06-04
 * ==========================================================================*/

(function () {
  'use strict';

  var KLAVIYO_PUBLIC_KEY = 'RSj9WC';
  var KLAVIYO_LIST_ID = 'TMYxZn';
  var KLAVIYO_ENDPOINT =
    'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY;
  var KLAVIYO_EVENTS_ENDPOINT =
    'https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_PUBLIC_KEY;
  var KLAVIYO_REVISION = '2024-10-15';

  // Permanent, append-only log of every request — powers the per-product
  // "most wanted" breakdown in Klaviyo Analytics (unlike profile properties,
  // which overwrite). Shows up under Analytics > Metrics with this name.
  var KLAVIYO_EVENT_METRIC = 'Back In Stock Requested';

  // Atelier emits 'variant:update' (NOT 'variant:change') with resolved data.
  var VARIANT_UPDATE_EVENT = 'variant:update';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Web component wrapper. One instance per buy-buttons block.
   */
  class BackInStock extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-bis-dialog]');
      this.form = this.querySelector('[data-bis-form]');
      this.emailInput = this.querySelector('input[name="email"]');
      this.message = this.querySelector('[data-bis-message]');
      this.submitBtn = this.querySelector('[data-bis-submit]');
      this.labelEl = this.querySelector('[data-bis-label]');

      var openBtn = this.querySelector('[data-bis-open]');
      var closeBtn = this.querySelector('[data-bis-close]');

      if (openBtn) openBtn.addEventListener('click', this.open.bind(this));
      if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Close when clicking the backdrop.
      if (this.dialog) {
        this.dialog.addEventListener('click', function (e) {
          if (e.target === this.dialog) this.close();
        }.bind(this));
      }

      // React to size/variant changes on the surrounding section.
      this.section = this.closest('.shopify-section, dialog');
      if (this.section) {
        this.section.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
      }
    }

    disconnectedCallback() {
      if (this.section) {
        this.section.removeEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
      }
    }

    /**
     * On size change, mirror the freshly server-rendered <back-in-stock>:
     * its [hidden] attribute (in stock vs sold out) and its variant data.
     */
    handleVariantUpdate = (event) => {
      var detail = event.detail || {};
      var data = detail.data || {};

      // Multi-product safety guard (combined listings / quick-add dialogs).
      var eventProductId = (data.productId != null)
        ? data.productId
        : (data.newProduct && data.newProduct.id);
      if (eventProductId && String(eventProductId) !== String(this.dataset.productId)) {
        return;
      }

      var html = data.html;
      if (!html) return;

      // html may be a parsed DOM node (Atelier) or, defensively, a string.
      var fresh;
      if (typeof html === 'string') {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        fresh = doc.querySelector('back-in-stock');
      } else if (typeof html.querySelector === 'function') {
        fresh = html.querySelector('back-in-stock');
      }

      if (!fresh) {
        // No back-in-stock in the new render → treat as buyable, hide.
        this.hidden = true;
        return;
      }

      // Mirror hidden state + sync variant data so the email is tagged with
      // the size the customer is actually looking at.
      this.hidden = fresh.hasAttribute('hidden');

      var newVariantId = fresh.getAttribute('data-variant-id');
      var newLabel = fresh.getAttribute('data-product-label');
      if (newVariantId) this.dataset.variantId = newVariantId;
      if (newLabel != null) {
        this.dataset.productLabel = newLabel;
        if (this.labelEl) this.labelEl.textContent = newLabel;
      }

      // Clear any stale success/error message when the size changes.
      this.setMessage('', '');
    };

    open() {
      this.setMessage('', '');
      if (this.dialog && typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else if (this.dialog) {
        this.dialog.setAttribute('open', '');
      }
      if (this.emailInput) this.emailInput.focus();
    }

    close() {
      if (this.dialog && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else if (this.dialog) {
        this.dialog.removeAttribute('open');
      }
    }

    setMessage(text, type) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.className = 'custom-bis__message' + (type ? ' custom-bis__message--' + type : '');
    }

    onSubmit(e) {
      e.preventDefault();

      var email = (this.emailInput && this.emailInput.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.setMessage('Please enter a valid email address.', 'error');
        return;
      }

      var label = this.dataset.productLabel || '';
      var variantId = this.dataset.variantId || '';

      this.submitBtn.disabled = true;
      this.setMessage('Submitting…', '');

      // Record the request as a permanent event (fire-and-forget — never block
      // the customer's success on analytics). The list subscribe below is the
      // primary action that determines success/failure.
      this.trackEvent(email, label, variantId).catch(function (err) {
        console.warn('[back-in-stock] event tracking failed:', err);
      });

      this.subscribe(email, label, variantId)
        .then(function () {
          this.setMessage("You're on the list! We'll email you when it's back.", 'success');
          if (this.form) this.form.reset();
        }.bind(this))
        .catch(function () {
          this.setMessage('Something went wrong. Please try again.', 'error');
        }.bind(this))
        .then(function () {
          this.submitBtn.disabled = false;
        }.bind(this));
    }

    /**
     * Log a "Back In Stock Requested" event in Klaviyo. Append-only — one
     * record per submission — so the same person requesting multiple items
     * (or the same item over time) is fully preserved. Product/size live in
     * the event properties, enabling a per-product breakdown in Analytics.
     * Klaviyo returns 202 (empty body) on success.
     */
    trackEvent(email, label, variantId) {
      var payload = {
        data: {
          type: 'event',
          attributes: {
            metric: {
              data: {
                type: 'metric',
                attributes: { name: KLAVIYO_EVENT_METRIC }
              }
            },
            properties: {
              Product: label,
              'Variant ID': variantId
            },
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          }
        }
      };

      return fetch(KLAVIYO_EVENTS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('Klaviyo event failed: ' + res.status);
        return res;
      });
    }

    /**
     * Subscribe the email to the Klaviyo list, tagging the requested item.
     * Klaviyo returns 202 (empty body) on success.
     */
    subscribe(email, label, variantId) {
      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    'Requested Restock': label,
                    'Requested Variant ID': variantId
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: { type: 'list', id: KLAVIYO_LIST_ID }
            }
          }
        }
      };

      return fetch(KLAVIYO_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('Klaviyo subscribe failed: ' + res.status);
        return res;
      });
    }
  }

  if (!customElements.get('back-in-stock')) {
    customElements.define('back-in-stock', BackInStock);
  }
})();
